import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, Sparkles, ChevronRight, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { AIProcessor } from "@/components/AIProcessor";
import { createListing, CURRENT_USER, type DressCategory, type DressSize, type DressCondition } from "@/data/listings";
import { useToast } from "@/hooks/use-toast";

const CATEGORIES: { value: DressCategory; label: string }[] = [
  { value: "wedding", label: "Wedding" },
  { value: "engagement", label: "Engagement" },
  { value: "party", label: "Party" },
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
];
const SIZES: DressSize[] = ["XS", "S", "M", "L", "XL", "XXL"];
const CONDITIONS: { value: DressCondition; label: string; desc: string }[] = [
  { value: "like_new", label: "Like New", desc: "Worn once, no flaws" },
  { value: "excellent", label: "Excellent", desc: "Worn 2–3 times" },
  { value: "good", label: "Good", desc: "Visible light wear" },
  { value: "fair", label: "Fair", desc: "Noticeable wear" },
];

type Step = "upload" | "processing" | "details" | "done";

const PRICE_SUGGESTIONS: Record<DressCondition, number> = {
  like_new: 0.45,
  excellent: 0.35,
  good: 0.25,
  fair: 0.15,
};

const TITLE_SUGGESTIONS = [
  "Ivory Lace Midi Wedding Guest Dress",
  "Blush Satin Maxi – Engagement Party",
  "Emerald Green Mini Party Dress",
  "Rose Gold Sequin Evening Gown",
  "Navy Blue Floral Wrap Dress",
  "Champagne Off-Shoulder Formal Gown",
];

export default function Sell() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [enhancedUrls, setEnhancedUrls] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: "",
    category: "" as DressCategory,
    brand: "",
    size: "" as DressSize,
    condition: "" as DressCondition,
    originalPrice: "",
    sellingPrice: "",
    eventWornAt: "",
    description: "",
  });

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming).slice(0, 5);
    setFiles(arr);
    setPreviews(arr.map(f => URL.createObjectURL(f)));
  };

  const removeFile = (i: number) => {
    setFiles(f => f.filter((_, idx) => idx !== i));
    setPreviews(p => p.filter((_, idx) => idx !== i));
  };

  const startProcessing = () => {
    if (files.length === 0) return;
    setStep("processing");
  };

  const onEnhanceDone = (urls: string[]) => {
    setEnhancedUrls(urls);
    setStep("details");
  };

  const suggestTitle = () => {
    const s = TITLE_SUGGESTIONS[Math.floor(Math.random() * TITLE_SUGGESTIONS.length)];
    setForm(f => ({ ...f, title: s }));
  };

  const autoPrice = () => {
    if (!form.originalPrice || !form.condition) return;
    const ratio = PRICE_SUGGESTIONS[form.condition];
    const suggested = Math.round(parseFloat(form.originalPrice) * ratio / 10) * 10;
    setForm(f => ({ ...f, sellingPrice: suggested.toString() }));
    toast({ title: `Suggested price: ${suggested} SAR based on similar listings` });
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.size || !form.condition || !form.sellingPrice) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const sampleImages = [
      "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&q=80",
      "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&q=80",
    ];
    await createListing({
      title: form.title,
      category: form.category,
      brand: form.brand,
      size: form.size,
      condition: form.condition,
      originalPrice: parseFloat(form.originalPrice) || 0,
      sellingPrice: parseFloat(form.sellingPrice),
      eventWornAt: form.eventWornAt,
      description: form.description,
      images: sampleImages,
      sellerId: CURRENT_USER.id,
      sellerName: CURRENT_USER.name,
      sellerAvatar: CURRENT_USER.avatar,
      location: CURRENT_USER.location,
      distanceKm: 0,
    });
    setSubmitting(false);
    setStep("done");
  };

  return (
    <div className="min-h-screen pb-28 md:pb-8">
      <AppHeader />

      <div className="max-w-2xl mx-auto px-4 mt-6">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {(["upload", "processing", "details"] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${
                  step === s ? "text-white" : step === "done" || (["upload", "processing", "details"].indexOf(step) > i) ? "text-white opacity-60" : "border border-border text-muted-foreground"
                }`}
                style={step === s || ["upload", "processing", "details"].indexOf(step) > i ? { background: "var(--rose)" } : {}}
              >
                {i + 1}
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 rounded-full transition-all ${["upload", "processing", "details"].indexOf(step) > i ? "bg-rose-400" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* Step: Upload */}
        {step === "upload" && (
          <div>
            <h1 className="text-xl font-bold mb-1">Upload Your Dress</h1>
            <p className="text-sm text-muted-foreground mb-6">Add 2–5 photos. Our AI will create professional product images.</p>

            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={e => handleFiles(e.target.files)} />

            {previews.length === 0 ? (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full aspect-video rounded-3xl border-2 border-dashed border-border hover:border-rose-300 flex flex-col items-center justify-center gap-3 transition-all hover:bg-rose-50/30"
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--rose-light)" }}>
                  <ImagePlus size={24} style={{ color: "var(--rose)" }} />
                </div>
                <div className="text-center">
                  <p className="font-medium text-sm">Tap to add photos</p>
                  <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG up to 10MB each · 2–5 photos</p>
                </div>
              </button>
            ) : (
              <div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {previews.map((url, i) => (
                    <div key={i} className="relative aspect-[3/4] rounded-xl overflow-hidden bg-muted">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {previews.length < 5 && (
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="aspect-[3/4] rounded-xl border-2 border-dashed border-border hover:border-rose-300 flex items-center justify-center transition-all"
                    >
                      <Upload size={18} className="text-muted-foreground" />
                    </button>
                  )}
                </div>

                <div className="p-4 rounded-2xl border border-border/60 mb-5" style={{ background: "var(--rose-light)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={14} style={{ color: "var(--rose)" }} />
                    <span className="text-sm font-semibold" style={{ color: "var(--rose)" }}>AI Ghost Mannequin</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We'll remove the background, erase the model, and generate a professional ghost mannequin effect — like Zara catalog photos.
                  </p>
                </div>

                <Button
                  className="w-full rounded-full h-12 gap-2 font-semibold text-sm"
                  style={{ background: "var(--rose)", color: "white" }}
                  onClick={startProcessing}
                >
                  <Sparkles size={15} />
                  Enhance with AI
                  <ChevronRight size={15} />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step: Processing */}
        {step === "processing" && (
          <div>
            <h1 className="text-xl font-bold mb-1">Enhancing Your Photos</h1>
            <p className="text-sm text-muted-foreground mb-6">Sit tight while our AI works its magic ✨</p>
            <AIProcessor images={files} onComplete={onEnhanceDone} />
          </div>
        )}

        {/* Step: Details */}
        {step === "details" && (
          <div>
            <h1 className="text-xl font-bold mb-1">Listing Details</h1>
            <p className="text-sm text-muted-foreground mb-6">Tell buyers about your dress.</p>

            {/* Enhanced preview */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
              {enhancedUrls.map((url, i) => (
                <div key={i} className="flex-shrink-0 w-24 aspect-[3/4] rounded-xl overflow-hidden relative">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-end p-1">
                    <span className="w-full text-center text-white text-xs bg-black/50 rounded-lg py-0.5">Enhanced</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Title *</Label>
                <div className="relative">
                  <Input
                    placeholder="Describe your dress…"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    className="pr-24 rounded-xl h-10 text-sm"
                  />
                  <button onClick={suggestTitle} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors" style={{ color: "var(--rose)", background: "var(--rose-light)" }}>
                    Suggest
                  </button>
                </div>
              </div>

              {/* Category */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Event / Category *</Label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setForm(f => ({ ...f, category: c.value }))}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        form.category === c.value
                          ? "border-rose-400 bg-rose-50 text-rose-600"
                          : "border-border/60 text-muted-foreground"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Size *</Label>
                <div className="flex flex-wrap gap-2">
                  {SIZES.map(s => (
                    <button
                      key={s}
                      onClick={() => setForm(f => ({ ...f, size: s }))}
                      className={`w-12 h-10 rounded-xl text-sm border transition-all font-medium ${
                        form.size === s
                          ? "border-rose-400 bg-rose-50 text-rose-600"
                          : "border-border/60 text-muted-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Condition */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Condition *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CONDITIONS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setForm(f => ({ ...f, condition: c.value }))}
                      className={`p-3 rounded-xl text-left border transition-all ${
                        form.condition === c.value
                          ? "border-rose-400 bg-rose-50"
                          : "border-border/60"
                      }`}
                    >
                      <p className={`text-sm font-medium ${form.condition === c.value ? "text-rose-600" : ""}`}>{c.label}</p>
                      <p className="text-xs text-muted-foreground">{c.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Brand (optional)</Label>
                <Input placeholder="Zara, H&M, Mango…" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} className="rounded-xl h-10 text-sm" />
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Original Price (SAR)</Label>
                  <Input placeholder="1200" type="number" value={form.originalPrice} onChange={e => setForm(f => ({ ...f, originalPrice: e.target.value }))} className="rounded-xl h-10 text-sm" />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Selling Price (SAR) *</Label>
                  <div className="relative">
                    <Input
                      placeholder="350"
                      type="number"
                      value={form.sellingPrice}
                      onChange={e => setForm(f => ({ ...f, sellingPrice: e.target.value }))}
                      className="pr-20 rounded-xl h-10 text-sm"
                    />
                    {form.originalPrice && form.condition && (
                      <button onClick={autoPrice} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium px-2 py-1 rounded-lg" style={{ color: "var(--rose)", background: "var(--rose-light)" }}>
                        Auto
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Event */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Event worn at (optional)</Label>
                <Input placeholder="e.g. Sara's wedding, June 2024" value={form.eventWornAt} onChange={e => setForm(f => ({ ...f, eventWornAt: e.target.value }))} className="rounded-xl h-10 text-sm" />
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Description</Label>
                <Textarea placeholder="Share the story of this dress…" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-xl text-sm resize-none" rows={3} />
              </div>

              <Button
                className="w-full rounded-full h-12 font-semibold text-sm gap-2"
                style={{ background: "var(--rose)", color: "white" }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Publishing…" : "Publish Listing"}
                {!submitting && <ChevronRight size={15} />}
              </Button>
            </div>
          </div>
        )}

        {/* Done */}
        {step === "done" && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold mb-2">Your dress is live!</h2>
            <p className="text-muted-foreground text-sm mb-8 max-w-xs mx-auto">
              Buyers near you can now discover your listing. We'll notify you when someone messages you.
            </p>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Button className="rounded-full h-12 font-medium" style={{ background: "var(--rose)", color: "white" }} onClick={() => navigate("/")}>
                Browse Discover Feed
              </Button>
              <Button variant="outline" className="rounded-full h-12 font-medium" onClick={() => { setStep("upload"); setFiles([]); setPreviews([]); }}>
                Sell Another Dress
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
